import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const TagInput = ({ value = [], onChange, suggestions = [], error }) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (inputValue?.trim()) {
      const filtered = suggestions?.filter(
        (tag) =>
          tag?.toLowerCase()?.includes(inputValue?.toLowerCase()) &&
          !value?.includes(tag)
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered?.length > 0);
    } else {
      setShowSuggestions(false);
    }
  }, [inputValue, suggestions, value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef?.current && !containerRef?.current?.contains(event?.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddTag = (tag) => {
    if (tag?.trim() && !value?.includes(tag?.trim())) {
      onChange([...value, tag?.trim()]);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && inputValue?.trim()) {
      e?.preventDefault();
      handleAddTag(inputValue);
    } else if (e?.key === 'Backspace' && !inputValue && value?.length > 0) {
      onChange(value?.slice(0, -1));
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    onChange(value?.filter((tag) => tag !== tagToRemove));
  };

  return (
    <div className="w-full" ref={containerRef}>
      <div className="min-h-[40px] md:min-h-[48px] rounded-lg bg-input border border-border px-2 md:px-3 py-2 flex flex-wrap gap-2">
        {value?.map((tag, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-1.5 px-2.5 md:px-3 py-1 md:py-1.5 rounded-md bg-primary/10 text-primary text-xs md:text-sm font-medium"
          >
            {tag}
            <button
              type="button"
              onClick={() => handleRemoveTag(tag)}
              className="hover:bg-primary/20 rounded-sm p-0.5 transition-colors"
              aria-label={`Remove ${tag} tag`}
            >
              <Icon name="X" size={14} color="currentColor" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e?.target?.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue?.trim() && setShowSuggestions(filteredSuggestions?.length > 0)}
          placeholder={value?.length === 0 ? 'Add tags...' : ''}
          className="flex-1 min-w-[120px] bg-transparent border-0 outline-none text-sm md:text-base text-foreground placeholder:text-muted-foreground"
        />
      </div>
      {showSuggestions && (
        <div className="mt-2 rounded-lg bg-popover border border-border shadow-soft-md max-h-48 overflow-y-auto">
          {filteredSuggestions?.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleAddTag(suggestion)}
              className="w-full px-3 md:px-4 py-2 md:py-2.5 text-left text-sm md:text-base text-popover-foreground hover:bg-muted transition-colors"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
      {error && (
        <p className="text-xs md:text-sm text-error mt-1.5" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default TagInput;